/**
 * Iter-292g — Shared "any modal open?" detector.
 *
 * Why this exists: WelcomeVideo (and several other dashboards' first-time
 * modals) use Radix Dialog from shadcn UI. Radix portals its content to
 * document.body and applies `pointer-events: none` to everything outside
 * its tree while open. If a separate non-Radix modal (e.g. the Community
 * Intro popup) opens at the same time, its buttons LOOK clickable but
 * Radix swallows the pointer events — the user is trapped.
 *
 * `useNoOtherDialogOpen(active)` polls for Radix dialogs (or any
 * `[role="dialog"]` with `data-state="open"`) and returns true only when
 * none are present. Use it to gate the *opening* of any non-Radix modal:
 *
 *     const safeToOpen = useNoOtherDialogOpen(!introSeen);
 *     useEffect(() => { if (safeToOpen) setShowCommunityIntro(true); }, [safeToOpen]);
 */
import { useEffect, useState } from 'react';

const _selector = '[role="dialog"][data-state="open"], [data-state="open"][role="dialog"]';

function _anyDialogOpen(ownTestId) {
  if (typeof document === 'undefined') return false;
  const nodes = document.querySelectorAll(_selector);
  for (const n of nodes) {
    // Allow our own modal — when CommunityIntro is already open, we don't
    // want to consider it as a "blocker" against itself.
    if (ownTestId && n.getAttribute('data-testid') === ownTestId) continue;
    // Skip nodes hidden by aria/visibility — Radix sets data-state="open"
    // briefly during close animations.
    if (n.getAttribute('aria-hidden') === 'true') continue;
    return true;
  }
  return false;
}

export function useNoOtherDialogOpen(activeWhen, ownTestId) {
  const [safe, setSafe] = useState(false);
  useEffect(() => {
    if (!activeWhen) {
      setSafe(false);
      return;
    }
    // First check immediately.
    setSafe(!_anyDialogOpen(ownTestId));
    // Watch DOM mutations so we react when other modals close.
    const obs = new MutationObserver(() => {
      const isSafe = !_anyDialogOpen(ownTestId);
      setSafe((cur) => (cur === isSafe ? cur : isSafe));
    });
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state', 'aria-hidden'] });
    return () => obs.disconnect();
  }, [activeWhen, ownTestId]);
  return safe;
}
